import type { Units } from '@better-lights/core';
import {
  DEFAULT_SETTINGS,
  migrateSettings,
  normalizeLegendKeys,
  SETTINGS_VERSION,
  type SettingsData,
} from '@better-lights/core';
import { defaultUnits } from '../lib/units.ts';
import { db } from './db.ts';

/** Web-only settings (device-local). */
export interface WebSettings {
  /** Estimate scene depth on this device (downloads a ~27–50 MB model once). */
  depthEnabled: boolean;
  exportFormat: 'jpeg' | 'png';
  /**
   * Imperial or metric display/input (docs/spec/09 §2). Default from the device region. Synced in
   * the personal catalog (`units`) when signed in. INTERIM here until core's SettingsData has it.
   */
  units: Units;
}

export type AppSettings = SettingsData & WebSettings;

export const DEFAULT_WEB_SETTINGS: WebSettings = { depthEnabled: true, exportFormat: 'jpeg', units: defaultUnits() };
export const DEFAULT_APP_SETTINGS: AppSettings = { ...DEFAULT_SETTINGS, ...DEFAULT_WEB_SETTINGS };

const KEY = 'better-lights.settings.v1';

/** Persisted with the original's envelope `{ state, version }` (01 §4), in IndexedDB. */
export async function loadSettings(): Promise<AppSettings> {
  const row = await db().kv.get(KEY);
  const env = row?.value as { state?: Record<string, unknown>; version?: number } | undefined;
  if (!env?.state) return { ...DEFAULT_APP_SETTINGS };
  const state = (env.version ?? 0) < SETTINGS_VERSION ? migrateSettings(env.state, env.version ?? 0).state : env.state;
  const merged = { ...DEFAULT_APP_SETTINGS, ...(state as Partial<AppSettings>) };
  return { ...merged, legendLabels: normalizeLegendKeys(merged.legendLabels) };
}

export async function saveSettings(s: AppSettings): Promise<void> {
  await db().kv.put({ key: KEY, value: { state: s, version: SETTINGS_VERSION } });
}
